import { useCallback, useEffect, useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import { NumbersHealth } from './NumbersHealth';
import { NumbersSettings } from './NumbersSettings';
import { useModalDismiss } from './use-modal-dismiss';
import {
  fetchPhoneNumberPerformance,
  type PhoneNumberPerformance,
  type PhoneNumberPerformanceResponse,
  type ProbationVerdict,
} from './api';

// =====================================================================
// Numbers dashboard — how each caller ID is actually performing.
//
// This is a TABLE, not a chart, on purpose: the job is comparing many numbers
// across a few metrics and acting on the outliers, which is what a sortable table
// does. There is no time series to plot — attribution (`call_events.phone_id`)
// only starts when it was wired, so early on there is little history and a trend
// line would be a decorative lie.
//
// THE RULE THIS SCREEN EXISTS TO ENFORCE: never show a rate without its sample.
// A 0% answer rate from 3 dials looks like a dead number and is nothing at all —
// render it as "not enough data yet". The `enough_data` flag comes from the core
// (one threshold, one place). Getting this wrong makes operators release good
// numbers, which is worse than showing nothing.
// =====================================================================

const WINDOWS = [7, 30, 90] as const;

type SortKey = 'number' | 'dials' | 'answer_rate' | 'short_call_rate' | 'usage';

// Status is a STATE, not a series — so it gets the reserved status palette and always
// ships with its label, never colour alone.
const STATUS_STYLES: Record<string, string> = {
  active: 'bg-emerald-100 text-emerald-800',
  cooling_down: 'bg-amber-100 text-amber-800',
  flagged: 'bg-red-100 text-red-800',
  retired: 'bg-muted text-muted-foreground',
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={cn(
        'rounded-full px-2 py-0.5 text-[0.65rem] font-medium whitespace-nowrap',
        STATUS_STYLES[status] ?? 'bg-muted text-muted-foreground',
      )}
    >
      {status.replace(/_/g, ' ')}
    </span>
  );
}

// Probation is about numbers we just bought (up to a third of fresh DIDs arrive
// already flagged). "new" is deliberately neutral wording, not a warning colour: a
// canary with no verdict yet is UNKNOWN, and dressing it as a problem is what makes
// someone release a number they paid for.
function ProbationBadge({ verdict, reason }: { verdict: ProbationVerdict; reason: string | null }) {
  if (verdict === 'not_applicable') return null;

  const label = verdict === 'probation' ? 'new' : verdict === 'passed' ? 'passed' : 'review';
  const style =
    verdict === 'failing'
      ? 'bg-red-100 text-red-800'
      : verdict === 'passed'
        ? 'bg-emerald-100 text-emerald-800'
        : 'bg-muted text-muted-foreground';

  return (
    <span
      title={reason ?? undefined}
      className={cn('rounded-full px-2 py-0.5 text-[0.65rem] font-medium whitespace-nowrap', style)}
    >
      {label}
    </span>
  );
}

function StatTile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-md border px-3 py-2">
      <div className="text-muted-foreground text-[0.65rem] tracking-wide uppercase">{label}</div>
      <div className="text-lg font-semibold tabular-nums">{value}</div>
      {hint && <div className="text-muted-foreground text-[0.65rem]">{hint}</div>}
    </div>
  );
}

const pct = (v: number | null): string => (v === null ? '—' : `${(v * 100).toFixed(0)}%`);

// A rate is only rendered when the sample earns it. Below the floor the number itself
// is withheld — not greyed out, not shown small — because a figure on screen gets
// acted on regardless of styling.
function RateCell({
  rate,
  enoughData,
  emphasis,
}: {
  rate: number | null;
  enoughData: boolean;
  emphasis?: 'low-is-bad' | 'high-is-bad';
}) {
  if (!enoughData) {
    return <span className="text-muted-foreground text-[0.65rem] italic">not enough data</span>;
  }
  if (rate === null) return <span className="text-muted-foreground">—</span>;

  const bad =
    emphasis === 'low-is-bad' ? rate < 0.05 : emphasis === 'high-is-bad' ? rate > 0.4 : false;

  return (
    <span className={cn('tabular-nums', bad && 'font-semibold text-red-700')}>{pct(rate)}</span>
  );
}

export function NumbersDashboard({ onClose }: { onClose: () => void }) {
  const dialog = useModalDismiss(onClose);
  const [data, setData] = useState<PhoneNumberPerformanceResponse | null>(null);
  const [windowDays, setWindowDays] = useState<number>(30);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({
    key: 'dials',
    dir: 'desc',
  });
  const [tab, setTab] = useState<'performance' | 'health' | 'settings'>('performance');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await fetchPhoneNumberPerformance(windowDays));
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load number performance');
    } finally {
      setLoading(false);
    }
  }, [windowDays]);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = useMemo(() => {
    if (!data) return [];
    const dir = sort.dir === 'asc' ? 1 : -1;

    // Numbers without a real sample always sort BELOW those with one, whichever way
    // the column is sorted: an unproven number is not "the best" or "the worst", and
    // letting it top a rate sort is how it gets acted on.
    const rank = (n: PhoneNumberPerformance): number | null => {
      switch (sort.key) {
        case 'dials':
          return n.dials;
        case 'usage':
          return n.hourly_calls;
        case 'answer_rate':
          return n.enough_data ? n.answer_rate : null;
        case 'short_call_rate':
          return n.enough_data ? n.short_call_rate : null;
        default:
          return null;
      }
    };

    return [...data.phone_numbers].sort((a, b) => {
      if (sort.key === 'number') return a.number.localeCompare(b.number) * dir;
      const av = rank(a);
      const bv = rank(b);
      if (av === null && bv === null) return a.number.localeCompare(b.number);
      if (av === null) return 1;
      if (bv === null) return -1;
      return (av - bv) * dir;
    });
  }, [data, sort]);

  const toggleSort = (key: SortKey) =>
    setSort((s) => ({ key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }));

  const summary = data?.summary;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        {...dialog}
        aria-label="Numbers"
        className="bg-background flex max-h-[90vh] w-full max-w-5xl flex-col gap-3 rounded-lg border p-4 shadow-lg focus:outline-none"
      >
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold">Numbers</h2>
            <p className="text-muted-foreground text-xs">
              {tab === 'performance'
                ? `What actually happened on each caller ID's calls. Rates need at least ${data?.min_sample ?? 30} dials before they mean anything.`
                : tab === 'health'
                  ? 'What the dialer believes about each number — the four scores it picks on.'
                  : 'What we’re buying, and the knobs that used to need a deploy.'}
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-xs">
            Close
          </button>
        </div>

        <div className="flex items-center gap-1 border-b">
          {(['performance', 'health', 'settings'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={cn(
                '-mb-px border-b-2 px-2 py-1 text-xs',
                tab === t
                  ? 'border-foreground font-medium'
                  : 'text-muted-foreground border-transparent',
              )}
            >
              {t === 'performance'
                ? 'Performance'
                : t === 'health'
                  ? 'Health'
                  : 'Buying & settings'}
            </button>
          ))}
        </div>

        {tab === 'settings' ? (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <NumbersSettings />
          </div>
        ) : tab === 'health' ? (
          <NumbersHealth rows={rows} minSample={data?.min_sample ?? 30} />
        ) : (
          <>
            <div className="flex items-center gap-1">
              {WINDOWS.map((w) => (
                <button
                  key={w}
                  onClick={() => setWindowDays(w)}
                  className={cn(
                    'rounded-md border px-2 py-1 text-xs',
                    windowDays === w ? 'bg-muted font-medium' : 'text-muted-foreground',
                  )}
                >
                  {w}d
                </button>
              ))}
              <button
                onClick={() => void load()}
                className="text-muted-foreground hover:text-foreground ml-auto text-xs"
              >
                Refresh
              </button>
            </div>

            {err && (
              <div className="bg-destructive/10 text-destructive rounded-md p-2 text-xs">{err}</div>
            )}

            {summary && (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <StatTile label="Numbers" value={String(summary.total)} />
                <StatTile label={`Dials (${windowDays}d)`} value={summary.dials.toLocaleString()} />
                <StatTile
                  label="Fleet answer rate"
                  value={pct(summary.answer_rate)}
                  // The fleet rate is the baseline a single number should be read against —
                  // "bad" is relative to peers dialing comparable lists, not an absolute.
                  hint="the baseline to compare against"
                />
                <StatTile
                  label="Needs attention"
                  value={String(summary.needs_attention)}
                  hint="flagged, or proven low"
                />
              </div>
            )}

            <div className="min-h-0 flex-1 overflow-y-auto rounded-md border">
              <table className="w-full text-left text-xs">
                <thead className="bg-muted/50 text-muted-foreground sticky top-0">
                  <tr>
                    <Th onClick={() => toggleSort('number')} active={sort.key === 'number'}>
                      Number
                    </Th>
                    <th className="px-2 py-1 font-medium">Status</th>
                    <th className="px-2 py-1 font-medium">Region</th>
                    <Th onClick={() => toggleSort('dials')} active={sort.key === 'dials'} numeric>
                      Dials
                    </Th>
                    <Th
                      onClick={() => toggleSort('answer_rate')}
                      active={sort.key === 'answer_rate'}
                      numeric
                    >
                      Answer rate
                    </Th>
                    <Th
                      onClick={() => toggleSort('short_call_rate')}
                      active={sort.key === 'short_call_rate'}
                      numeric
                    >
                      {/* Name the threshold. A bare "Short calls %" gives an operator
                          nothing to act on until they know what counts as short — and it
                          was only discoverable by reading a ClickHouse query. */}
                      Short calls (&lt;{data?.short_call_seconds ?? 15}s)
                    </Th>
                    <Th onClick={() => toggleSort('usage')} active={sort.key === 'usage'} numeric>
                      This hour
                    </Th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((n) => (
                    <tr key={n.id} className="border-t">
                      <td className="px-2 py-1 font-mono whitespace-nowrap">{n.number}</td>
                      <td className="px-2 py-1">
                        <div className="flex items-center gap-1">
                          <StatusBadge status={n.status} />
                          <ProbationBadge verdict={n.probation} reason={n.probation_reason} />
                        </div>
                      </td>
                      <td className="text-muted-foreground px-2 py-1">{n.region ?? '—'}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{n.dials}</td>
                      <td className="px-2 py-1 text-right">
                        <RateCell
                          rate={n.answer_rate}
                          enoughData={n.enough_data}
                          emphasis="low-is-bad"
                        />
                      </td>
                      <td className="px-2 py-1 text-right">
                        {/* Short calls are rated over ANSWERED calls, so they need answers to
                        have happened — not just dials — before the rate means anything. */}
                        <RateCell
                          rate={n.short_call_rate}
                          enoughData={n.enough_data && n.answered > 0}
                          emphasis="high-is-bad"
                        />
                      </td>
                      <td
                        className="px-2 py-1 text-right tabular-nums"
                        title={`Hourly cap ${n.turn_cap}. Counted across every pool — one number, one budget.`}
                      >
                        <span
                          className={cn(
                            n.hourly_calls >= n.turn_cap && 'font-semibold text-amber-700',
                          )}
                        >
                          {n.hourly_calls}
                        </span>
                        <span className="text-muted-foreground">/{n.turn_cap}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {!loading && rows.length === 0 && (
                <div className="text-muted-foreground p-4 text-center text-xs">
                  No caller IDs yet.
                </div>
              )}
              {loading && !data && (
                <div className="text-muted-foreground p-4 text-center text-xs">Loading…</div>
              )}
            </div>

            <p className="text-muted-foreground text-[0.65rem]">
              Only calls placed since per-number tracking was enabled are counted, so history before
              that is not shown. Usage is counted across every pool a number serves.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function Th({
  children,
  onClick,
  active,
  numeric,
}: {
  children: React.ReactNode;
  onClick: () => void;
  active: boolean;
  numeric?: boolean;
}) {
  return (
    <th className={cn('px-2 py-1 font-medium', numeric && 'text-right')}>
      <button
        onClick={onClick}
        className={cn('hover:text-foreground', active && 'text-foreground font-semibold')}
      >
        {children}
      </button>
    </th>
  );
}
